import React, { useState, useEffect, useRef } from 'react';
import { BookOpen, Building, Filter, Languages, LineChart, MessageSquareMore } from 'lucide-react';
import { FeatureCard } from './FeatureCard';

export function FeatureGrid() {
  const [visibleCards, setVisibleCards] = useState<number[]>([]);
  const gridRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          // Reveal cards with staggered timing
          features.forEach((_, index) => {
            setTimeout(() => {
              setVisibleCards(prev => [...prev, index]);
            }, index * 80); // 80ms between each card appearance
          });
          
          observer.disconnect();
        }
      },
      { threshold: 0.1 } // Trigger when 10% of the grid is visible
    );

    if (gridRef.current) {
      observer.observe(gridRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  const features = [
    {
      icon: <Filter className="w-8 h-8" />,
      title: "Personalized Practice",
      description: "Get personalized practice interviews, based on your resume, experience, and more."
    },
    {
      icon: <Building className="w-8 h-8" />,
      title: "Industry-Specific Interviews",
      description: "You get to choose & practice for the company & job role you want to join."
    },
    {
      icon: <Languages className="w-8 h-8" />,
      title: "Multi-Language Support",
      description: "Practice in your native language, or in English, or both. MockTrail understands what you say."
    },
    {
      icon: <BookOpen className="w-8 h-8" />,
      title: "Daily Skill-Based News",
      description: "Learn from daily skill-based news for your role, you don't need to open unread tabs."
    },
    {
      icon: <MessageSquareMore className="w-8 h-8" />,
      title: "Communicate In Your Way",
      description: "Our AI Interviewer listens to you, sees your video camera, screen or a simple text."
    },
    {
      icon: <LineChart className="w-8 h-8" />,
      title: "Statistics & Analytics",
      description: "Track how much time you spent practicing, most interviewed companies, roles & more."
    }
  ];

  return (
    <div 
      ref={gridRef}
      className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto"
    >
      {features.map((feature, index) => (
        <div
          key={index}
          style={{
            opacity: visibleCards.includes(index) ? 1 : 0,
            transform: visibleCards.includes(index) 
              ? 'translateY(0) scale(1)' 
              : 'translateY(20px) scale(0.95)',
            transition: `all 0.5s cubic-bezier(0.16,1,0.3,1) ${index * 0.05}s`
          }}
        >
          <FeatureCard {...feature} />
        </div>
      ))}
    </div>
  );
}